import { postDocumentsUpload } from "services/api/taptype/pdf";
import { apiHandler, errorResponseInterceptor, successResponseInterceptor } from "./api";

/** Shown on the combined action when the iframe/query token is absent (upload cannot run). */
export const JUSTMEDS_MISSING_CONSULTATION_TOKEN_TITLE =
	"Consultation token is missing. This note may not have an associated consultation yet.";

const MISSING_TOKEN_UPLOAD_MESSAGE =
	"Consultation token is missing. This note may not have an associated consultation yet. Please contact support or ensure the consultation is created before uploading documents.";

/**
 * Resolves the Taptype consultation token from note payload, URL, or navigation state.
 * Multiple sources exist because whitelabel flows embed the app with different entry points.
 */
export function resolveTaptypeConsultationToken(note, queryParams, locationState) {
	return (
		note?.iframe_token_id ||
		queryParams?.token ||
		queryParams?.iframe_token_id ||
		locationState?.token ||
		locationState?.iframe_token_id ||
		null
	);
}

export function resolveConsultationPatientId(note) {
	return note?.patient_id || note?.patient_plain_text?.id || "";
}

function buildMissingConsultationTokenError() {
	const error = new Error(MISSING_TOKEN_UPLOAD_MESSAGE);
	error.response = {
		data: { message: MISSING_TOKEN_UPLOAD_MESSAGE },
		status: 400,
		statusText: "Bad Request",
	};
	return error;
}

const interceptorsOpts = (dispatch, navigate, language, t) => ({
	dispatch,
	navigate,
	language,
	t,
});

function pickFailureMessage(responseError) {
	if (responseError == null) return undefined;
	if (typeof responseError === "string") return responseError;
	const m =
		responseError?.message ||
		responseError?.data?.message ||
		responseError?.response?.data?.message ||
		(typeof responseError?.data === "string" ? responseError.data : undefined);
	return typeof m === "string" ? m : undefined;
}

/**
 * Uploads generated PDF blobs to the JustMeds consultation documents endpoint.
 * @returns {Promise<{ ok: boolean, failureMessage?: string }>}
 */
export async function uploadJustmedsConsultationDocuments({
	pdfs,
	taptypeToken,
	patientId,
	dispatch,
	navigate,
	language,
	t,
	onUploadSuccess,
}) {
	if (!taptypeToken) {
		errorResponseInterceptor(
			buildMissingConsultationTokenError(),
			interceptorsOpts(dispatch, navigate, language, t)
		);
		return { ok: false, failureMessage: MISSING_TOKEN_UPLOAD_MESSAGE };
	}

	const formData = new FormData();
	formData.append("token", taptypeToken);
	formData.append("patient_id", patientId);
	pdfs.forEach((pdf) => {
		formData.append(pdf.key, pdf.blob, pdf.filename);
	});

	const opts = interceptorsOpts(dispatch, navigate, language, t);
	let ok = false;
	let failureMessage;

	await apiHandler(postDocumentsUpload, {
		data: { body: formData },
		onError: (responseError) => {
			errorResponseInterceptor(responseError, opts);
			failureMessage = pickFailureMessage(responseError);
		},
		onSuccess: (response) => {
			successResponseInterceptor(response, {
				dispatch,
				language,
				t,
			});
			ok = true;
			if (typeof onUploadSuccess === "function") onUploadSuccess(response);
		},
	});

	return ok ? { ok: true } : { ok: false, failureMessage };
}
